/**
 * What the mayor spends on nothing.
 *
 * `DESIGN-GOVERNORS.md` §10 lists three construction items beside the pavement,
 * and it lists them under a sentence that is the whole reason the district has a
 * budget at all:
 *
 * > | **Wildlife undercrossing** | High, one-time | 0 | Ants path beneath a road
 * >   segment; cars can't kill what crosses there |
 * > | **Meadow covenant** | Moderate + permanence | 0 | Cells locked NATURAL
 * >   forever; bushes persist |
 * > | **Hedgerow** | Low, slow | 0 | Plant bushes on unsealed land; new food
 * >   sources grow over seasons. Paved land can never be unsealed — nurture is
 * >   possible, undoing is not |
 * >
 * > None of these pay. That is the point.
 *
 * The `0` column is load-bearing and it is enforced here by absence: there is no
 * yield, no uptime, no capacity, no score, and no method on this class that returns
 * money. Every other manager in the surface hands something back to somebody — the
 * estate pays rent, the market pays wages, the grid delivers kilowatts. This one
 * takes a price and gives the district three facts about its own ground.
 *
 * ## What each of the three actually is, mechanically
 *
 *   1. **A covenant** is a refusal, held against the mayor's own future self. A
 *      deeded cell drops out of `PAVE` and out of the house spawner's candidate
 *      list, permanently. Nothing else in the district changes. Its price is
 *      therefore not the dollars — see below — but the ground, and ground is the
 *      one input this district's growth genuinely cannot substitute for.
 *   2. **A hedgerow** is a bush that is not there yet. It is booked as a sapling,
 *      comes in `HEDGEROW_CYCLES` budget cycles later, and from that moment is an
 *      ordinary bush in every respect: it is drawn, it bears fruit, it counts as
 *      standing meadow, and a forager can find it. It is the only thing a mayor can
 *      build that *adds* to the district rather than converting it.
 *   3. **An undercrossing** is a cell where a car cannot kill what is crossing. The
 *      only build in the game whose effect is invisible on the surface: no traffic
 *      slows, no route changes, no figure in any panel moves. What changes is a
 *      number in a ledger this class cannot see.
 *
 * ## The dollars are measured and, today, inert
 *
 * §8's district budget is not built (`IMPLEMENTATION-GOVERNORS.md` §6, work item
 * 6). The treasury spends freely and services its debt by borrowing more, so the
 * pool is a scoreboard rather than a constraint: nothing in the simulation reads
 * it, refuses because of it, or degrades when it falls. A district two seasons old
 * is already a million dollars down and running exactly as well as it was.
 *
 * So the prices here buy no pressure yet, and saying otherwise would be the first
 * authored delta in this build. What they buy is the same thing `deliveredKwCycles`
 * bought a phase before anything billed for it (Law 5, *the simulation is the
 * court*): the measurement exists before the mechanic that reads it, and when §8
 * gives the treasury a real constraint, mercy is already priced into it and
 * compounding at `INTEREST_RATE` along with everything else.
 *
 * The mechanical costs — a cell that can never be developed, a purse spent on a
 * bush, twenty-four sealed cells that took a covenant with them — are all real
 * today, and none of them is money.
 *
 * ## What this class cannot see
 *
 * The ground, and a hash type. Not the houses, not the traffic, not the treasury,
 * and not — the point of the arrangement, and the sharpest instance of it in the
 * package — anything under the soil. An undercrossing is *for* the colony below and
 * this file has never heard of one: it records that a cell has a way under it, and
 * whoever is walking across that cell is somebody else's business.
 *
 * That is Law 7 read in the direction nobody usually reads it. The law says the
 * layer above cannot see the layer below; the obvious consequence is that a
 * governor cannot count ants. The less obvious one is that **a mayor cannot be told
 * their mercy worked.** There is no counter here, or anywhere the surface can reach,
 * that says how many ants crossed safely. `tests/purity.test.ts` pins this file's
 * import list to keep it that way.
 *
 * See `docs/mercy-builds.md`.
 */

import { Canonical } from '../hashState';
import { CellState, GridCoord, TerrainGrid } from './TerrainGrid';

/**
 * What mercy costs. World parameters, not state.
 *
 * §10 gives three qualitative prices — *"High, one-time"*, *"Moderate +
 * permanence"*, *"Low, slow"* — so the numbers are chosen, and what they are chosen
 * against is the district's own tax take: a measured season is about $15,000 of tax
 * across ninety cycles, or $167 a cycle.
 *
 * That scale is the only honest yardstick available, since the pool is unbounded
 * (see the header). It puts a covenanted meadow of a dozen cells at roughly what
 * §7's worked exchange has a governor *offering* for a facility's lease, which is
 * the comparison the design cares about: a mayor choosing between a season's rent
 * and a field.
 */
export interface MercyConfig {
  /** Dollars to deed one cell wild. *"Moderate + permanence"* — two cycles of tax. */
  COVENANT_COST: number;
  /** Dollars to plant one bush. *"Low"* — half a cycle of tax. */
  HEDGEROW_COST: number;
  /** Dollars for one way under one paved cell. *"High, one-time"* — a week of tax. */
  UNDERCROSSING_COST: number;
  /**
   * Budget cycles from planting to a bush that bears fruit. *"Slow."*
   *
   * A third of a season, so a hedgerow planted early in a season is feeding
   * somebody before it closes and one planted in answer to a drought is not. §10
   * says *"new food sources grow over seasons"*, and the plural is about the habit
   * rather than about one bush: nothing in this game grows for two hundred and
   * seventy cycles.
   */
  HEDGEROW_CYCLES: number;
}

export const DEFAULT_MERCY_CONFIG: MercyConfig = {
  COVENANT_COST: 300,
  HEDGEROW_COST: 80,
  UNDERCROSSING_COST: 1200,
  HEDGEROW_CYCLES: 30,
};

/** Why a mercy build did not happen. Information for the caller, not an error. */
export type MercyRefusal =
  | { ok: false; reason: 'off-map' }
  /** Covenant or hedgerow on ground that is no longer natural. */
  | { ok: false; reason: 'developed' }
  /** A covenant on a cell that already has one. */
  | { ok: false; reason: 'deeded' }
  /** A hedgerow where a bush — grown or planted — already stands. */
  | { ok: false; reason: 'planted' }
  /** An undercrossing on ground with no road over it. */
  | { ok: false; reason: 'unpaved' }
  /** An undercrossing on a cell that already has one. */
  | { ok: false; reason: 'crossed' }
  /** A covenant on a cell the caller says has a building on it. */
  | { ok: false; reason: 'occupied' };

/** What a mercy build cost, or why there was not one. */
export type MercyResult = { ok: true; cost: number } | MercyRefusal;

/**
 * The district's own works, and what they came to.
 *
 * Monotone sums, except the three levels. A season's mercy is a subtraction over
 * two Chronicle lines, exactly as a season's energy is.
 */
export interface MercyLedger {
  /** Cells deeded wild, lifetime. Never falls. */
  covenantsRegistered: number;
  /** Bushes planted, lifetime. */
  hedgerowsPlanted: number;
  /** Planted bushes that came in. */
  hedgerowsGrown: number;
  /**
   * Saplings that never came in because the ground was developed first.
   *
   * §10's *"nurture is possible, undoing is not"* has a corollary nobody wrote
   * down: nurture can also be undone, by the person who paid for it, with a brush.
   * This is that, counted.
   */
  hedgerowsLost: number;
  /** Ways under a road, lifetime. */
  crossingsBuilt: number;
  /**
   * Cells of the mayor's works that a federal slab took, lifetime.
   *
   * §10 calls condemnation *"the most public aggression a governor can commit"*.
   * There is no Charter to gate it and no compensation to pay for it, so this
   * counter is the entire mechanical consequence, and it is here rather than in the
   * estate because it is a fact about what the district lost.
   */
  condemnedCells: number;
  /** Dollars committed to all of it, lifetime. Nothing was ever returned. */
  spent: number;
}

/** Everything the district's works are. See `MercyManager.save()`. */
export interface MercySnapshot {
  covenants: Array<{ x: number; y: number }>;
  saplings: Array<{ x: number; y: number; cycle: number }>;
  crossings: Array<{ x: number; y: number }>;
  ledger: MercyLedger;
}

function emptyLedger(): MercyLedger {
  return {
    covenantsRegistered: 0,
    hedgerowsPlanted: 0,
    hedgerowsGrown: 0,
    hedgerowsLost: 0,
    crossingsBuilt: 0,
    condemnedCells: 0,
    spent: 0,
  };
}

function key(x: number, y: number): string {
  return `${x},${y}`;
}

function cellsOf(keys: string[]): GridCoord[] {
  return keys.map((k) => {
    const [x, y] = k.split(',').map(Number);
    return { x, y };
  });
}

/**
 * The district's mercy builds.
 *
 * Owned by `SurfaceSim`, settled on the same budget cycle as the flows,
 * snapshotted inside it. Holds no `Rng`: a mayor decides where every one of these
 * goes and a calendar decides when a bush comes in, so there is nothing here to
 * roll — which makes this the fourth class in the surface that can say so.
 */
export class MercyManager {
  private config: MercyConfig;

  /** Cells deeded wild, by `"x,y"`. */
  private readonly covenants = new Set<string>();

  /** Bushes planted and not yet come in: cell → the cycle they were planted on. */
  private readonly saplings = new Map<string, number>();

  /** Cells with a way under them, by `"x,y"`. */
  private readonly crossings = new Set<string>();

  private ledger = emptyLedger();

  constructor(private readonly grid: TerrainGrid, config: Partial<MercyConfig> = {}) {
    this.config = { ...DEFAULT_MERCY_CONFIG, ...config };
  }

  getConfig(): MercyConfig {
    return { ...this.config };
  }

  updateConfig(partial: Partial<MercyConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  /** True while this district has never built any of the three. */
  get empty(): boolean {
    return this.covenants.size === 0
      && this.saplings.size === 0
      && this.crossings.size === 0
      && this.ledger.spent === 0;
  }

  // --- The three builds -----------------------------------------------------

  /**
   * Deed one cell wild, permanently.
   *
   * Refused on ground that is not `NATURAL`, which is the honest reading of §10's
   * *"cells locked NATURAL forever"*: a covenant is a promise about ground that is
   * still there, and there is no verb in this game that brings any back.
   *
   * Refused, too, on a cell the caller says has a building on it. §10 calls this a
   * *meadow* covenant, and a house's ground is not meadow — but *what counts as
   * built* is not this class's business any more than it is the estate's, so it
   * arrives as a predicate the way `FacilityManager.place`'s does. A district that
   * passes none admits every cell, which is a grid with no houses on it.
   *
   * Nothing here removes a covenant. `condemn` is the only thing that can, and a
   * governor is the only thing that calls it.
   */
  deed(
    x: number,
    y: number,
    built: (x: number, y: number) => boolean = () => false
  ): MercyResult {
    const cell = this.grid.getCell(x, y);
    if (!cell) return { ok: false, reason: 'off-map' };
    if (cell.state !== CellState.NATURAL) return { ok: false, reason: 'developed' };
    if (built(x, y)) return { ok: false, reason: 'occupied' };
    if (this.covenants.has(key(x, y))) return { ok: false, reason: 'deeded' };

    this.covenants.add(key(x, y));
    this.ledger.covenantsRegistered++;
    return this.charge(this.config.COVENANT_COST);
  }

  /**
   * Plant a bush on one cell, to come in later.
   *
   * Refused where a bush already stands, grown or planted, and on ground that is
   * not natural. A cell with a sapling on it already counts as planted, so paying
   * twice for the same hedgerow is not possible — which matters, because these are
   * brush tools and a drag passes over the same cell more than once.
   *
   * A covenanted cell is a perfectly good place for one, and is the combination the
   * design has in mind: the covenant is what stops the mayor paving over the bush
   * they paid for.
   */
  plant(x: number, y: number, cycle: number): MercyResult {
    const cell = this.grid.getCell(x, y);
    if (!cell) return { ok: false, reason: 'off-map' };
    if (cell.state !== CellState.NATURAL) return { ok: false, reason: 'developed' };
    if (cell.vegetationType === 'bush') return { ok: false, reason: 'planted' };
    if (this.saplings.has(key(x, y))) return { ok: false, reason: 'planted' };

    this.saplings.set(key(x, y), cycle);
    this.ledger.hedgerowsPlanted++;
    return this.charge(this.config.HEDGEROW_COST);
  }

  /**
   * Build a way under one paved cell.
   *
   * Paved, and refused otherwise: §10 says *"beneath a road segment"*, and a
   * crossing under a meadow is a crossing under nothing. That makes this the one
   * build in the game a mayor can only make on ground they have already destroyed,
   * which is the correct shape for it — an undercrossing is not a kindness, it is a
   * repair, and it costs the most of the three.
   *
   * A cell that is poured or curing counts. The concrete is down; the cars are
   * coming.
   */
  cross(x: number, y: number): MercyResult {
    if (!this.grid.getCell(x, y)) return { ok: false, reason: 'off-map' };
    if (!this.grid.isPavedAt(x, y)) return { ok: false, reason: 'unpaved' };
    if (this.crossings.has(key(x, y))) return { ok: false, reason: 'crossed' };

    this.crossings.add(key(x, y));
    this.ledger.crossingsBuilt++;
    return this.charge(this.config.UNDERCROSSING_COST);
  }

  private charge(cost: number): MercyResult {
    const amount = Math.max(0, cost);
    this.ledger.spent += amount;
    return { ok: true, cost: amount };
  }

  // --- What the district can ask -------------------------------------------

  /**
   * Whether this cell is deeded wild.
   *
   * Asked by the mayor's own brush on every click and by the house spawner on
   * every one of the twelve hundred cells it scans, which is why it is a set lookup
   * — and why it returns before building a key at all in the overwhelmingly common
   * case of a district that has never signed one. The three predicates below share
   * that guard for the same reason: this class must cost nothing at all in a world
   * where nobody has used it.
   */
  isDeeded(x: number, y: number): boolean {
    if (this.covenants.size === 0) return false;
    return this.covenants.has(key(x, y));
  }

  /**
   * Whether something crossing this cell is under the road rather than on it.
   *
   * The one question this class exists to answer and the one it deliberately does
   * not understand. It says a cell has a way under it. It does not know that
   * anything uses it, what uses it, or whether the use ever mattered.
   *
   * The hottest call in the file by a wide margin — every ant above ground asks it
   * up to twice a tick — hence the early return. Without it, a district with no
   * crossings pays for a string template per ant per tick to be told no.
   */
  shelters(x: number, y: number): boolean {
    if (this.crossings.size === 0) return false;
    return this.crossings.has(key(x, y));
  }

  /** Whether a bush has been paid for here and has not come in yet. */
  isSapling(x: number, y: number): boolean {
    if (this.saplings.size === 0) return false;
    return this.saplings.has(key(x, y));
  }

  /** Cells deeded wild right now. Falls only by condemnation. */
  get deededCells(): number {
    return this.covenants.size;
  }

  /** Bushes paid for and not yet standing. */
  get saplingCells(): number {
    return this.saplings.size;
  }

  /** Ways under a road right now. Falls only by condemnation. */
  get crossingCells(): number {
    return this.crossings.size;
  }

  /** The cells, for a renderer. Sorted, because everything here is. */
  deeded(): GridCoord[] {
    return cellsOf(Array.from(this.covenants).sort());
  }

  /** Saplings, for a renderer, with the cycle each was planted on. */
  planted(): Array<GridCoord & { cycle: number }> {
    return Array.from(this.saplings.keys())
      .sort()
      .map((k) => {
        const [x, y] = k.split(',').map(Number);
        return { x, y, cycle: this.saplings.get(k)! };
      });
  }

  /** Crossings, for a renderer. */
  crossed(): GridCoord[] {
    return cellsOf(Array.from(this.crossings).sort());
  }

  getLedger(): MercyLedger {
    return { ...this.ledger };
  }

  // --- The cycle -----------------------------------------------------------

  /**
   * Which hedgerows have come in, as of this budget cycle.
   *
   * Returns the cells and forgets them, in ascending cell order — a settlement's
   * output has to be a fixed sequence, because the caller turns each entry into a
   * bush and a food source and food sources are numbered in creation order
   * (`docs/determinism.md` rule 4).
   *
   * This class does not plant them. It cannot reach the ground's vegetation and it
   * certainly cannot reach a food source, so what it produces is a list and the
   * caller reports back with `recordGrown` and `recordLost` — the same split as the
   * estate's rent and the market's payroll, and for the same reason: the object
   * that knows what is owed is not the object that can pay it.
   */
  settle(cycle: number): GridCoord[] {
    if (this.saplings.size === 0) return [];

    const due: string[] = [];
    for (const k of Array.from(this.saplings.keys()).sort()) {
      if (cycle - this.saplings.get(k)! >= this.config.HEDGEROW_CYCLES) due.push(k);
    }
    for (const k of due) this.saplings.delete(k);
    return cellsOf(due);
  }

  /** Book hedgerows that actually took. */
  recordGrown(count: number): void {
    this.ledger.hedgerowsGrown += Math.max(0, count);
  }

  /** Book saplings whose ground was developed before they came in. */
  recordLost(count: number): void {
    this.ledger.hedgerowsLost += Math.max(0, count);
  }

  /**
   * A federal slab has taken this cell. Return whether it took any works with it.
   *
   * Everything on the cell goes: the covenant is void, the sapling will never come
   * in, the crossing is under twenty tonnes of foundation. §10 promises the covenant
   * is *"irreversible except by condemnation (Charter-gated)"* and this build has no
   * Charter, so there is no gate — a `FORCE_PLACE` seals deeded ground exactly as
   * easily as it seals anybody's meadow, and the only trace is `condemnedCells`.
   *
   * A cell counts once however many of the three were on it. The number is *cells
   * taken*, which is the number a mayor would recognise.
   */
  condemn(x: number, y: number): boolean {
    const k = key(x, y);
    // Three statements rather than a `||` chain, which would stop at the first
    // delete that succeeded and leave the other two standing under the slab.
    const deeded = this.covenants.delete(k);
    const sapling = this.saplings.delete(k);
    const crossing = this.crossings.delete(k);

    const had = deeded || sapling || crossing;
    if (had) this.ledger.condemnedCells++;
    return had;
  }

  // --- State ---------------------------------------------------------------

  save(): MercySnapshot {
    return {
      covenants: this.deeded(),
      saplings: this.planted().map((s) => ({ x: s.x, y: s.y, cycle: s.cycle })),
      crossings: this.crossed(),
      ledger: { ...this.ledger },
    };
  }

  load(state: MercySnapshot): void {
    this.covenants.clear();
    this.saplings.clear();
    this.crossings.clear();

    for (const cell of state.covenants) this.covenants.add(key(cell.x, cell.y));
    for (const s of state.saplings) this.saplings.set(key(s.x, s.y), s.cycle);
    for (const cell of state.crossings) this.crossings.add(key(cell.x, cell.y));
    this.ledger = { ...state.ledger };
  }

  /**
   * Order-stable view of the district's works, for hashing — and nothing when
   * there are none.
   *
   * The same move the estate, the flows, the market and the measure all make, and
   * with the same consequence: every golden master in this repository is a district
   * whose mayor never bought anything that did not pay, so ADR-020 moved none of
   * them.
   *
   * The covenant set is in the hash rather than derived, and it has to be: a deeded
   * cell is `NATURAL`, so it is invisible to the cell list `SurfaceSim` hashes.
   * Two districts that agree on every figure and disagree about which field is
   * spoken for are two different maps, and they diverge on the next `PAVE`.
   */
  canonicalState(): Canonical | undefined {
    if (this.empty) return undefined;
    return {
      covenants: Array.from(this.covenants).sort(),
      saplings: Array.from(this.saplings.keys())
        .sort()
        .map((k) => ({ cell: k, cycle: this.saplings.get(k)! })),
      crossings: Array.from(this.crossings).sort(),
      ledger: { ...this.ledger },
    };
  }
}
